import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Circle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";


function centroidOfOptions(opts, fallback) {
  if (!Array.isArray(opts) || !opts.length) return fallback;
  const sum = opts.reduce(
    (acc, o) => {
      const lat = Number(o?.lat);
      const lon = Number(o?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return acc;
      acc.lat += lat;
      acc.lon += lon;
      acc.n += 1;
      return acc;
    },
    { lat: 0, lon: 0, n: 0 }
  );
  return sum.n > 0 ? [sum.lat / sum.n, sum.lon / sum.n] : fallback;
}

const MapUpdater = ({ centerTo, zoomTo }) => {
  const map = useMap();
  useEffect(() => {
    if (Array.isArray(centerTo) && centerTo[0] != null && centerTo[1] != null) {
      map.setView(centerTo, zoomTo ?? map.getZoom(), { animate: false });
    }
  }, [centerTo, zoomTo, map]);
  return null;
};

const Map = ({
  mapConfig,
  options,
  value,
  onChange,
  nullLabel,
}) => {
  const { t } = useTranslation();
  const resolvedNullLabel = nullLabel || t("filters.all");
  const mc = mapConfig || {};

  const isExternalNull =
    value == null || value === "all" || value === resolvedNullLabel;

  const itemsToShow = isExternalNull
    ? (options || [])
    : (options || []).filter((o) => o?.name === value);

  const currentCenter = useMemo(() => {
    if (isExternalNull) {
      return centroidOfOptions(options, mc.center);
    }
    const found = (options || []).find((o) => o?.name === value);
    return (found && [found.lat, found.lon]) || mc.center;
  }, [isExternalNull, value, options, mc.center]);

  const tileUrl = useMemo(() => {
    if (mc.tileUrl) return mc.tileUrl;
    const provider = String(mc.provider || "").toLowerCase();
    const typeMap = String(mc.mapType || "").toLowerCase();
    if (provider.includes("openstreetmap")) {
      return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    }
    if (typeMap === "satellite") {
      return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
    }
    return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  }, [mc.tileUrl, mc.provider, mc.mapType]);

  if (!mc.center || !Array.isArray(mc.center) || mc.center.length !== 2) {
    return (
      <div className="makemake-map-error">
        {t("filters.invalidMapConfig")}
      </div>
    );
  }

  return (
    <div className="makemake-map-container">
      <div className="map-wrapper">
        <MapContainer
          center={mc.center || [0, 0]}
          zoom={mc.zoom || 13}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution={mc.attribution || "© OpenStreetMap"}
            url={tileUrl}
          />
          <MapUpdater centerTo={currentCenter} zoomTo={mc.zoom || 13} />
          {(itemsToShow || []).map((t, i) => (
            <Circle
              key={`${t.name}-${i}`}
              className="makemake-circle"
              center={[t.lat, t.lon]}
              radius={t.radius}
              eventHandlers={{
                click: () => {
                  const next = t?.name;
                  if (!next) return;
                  onChange?.(next);
                },
              }}
            />
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default Map;
